import React, { RefObject, createRef } from 'react';
import { Component } from 'react';
import { Center } from '../component';
import { screenShareApi, screenEvents, isPasteHotkey } from '../api/screen';
import ReactTooltip from 'react-tooltip';
import * as PIXI from 'pixi.js';
import _ from 'lodash';
import debounce from 'lodash/debounce';
import { logger } from '../api/logger';
import { inject, observer } from 'mobx-react';
import {
  ScreenShareHeader,
  ScreenShareToolbox,
} from '../component/screenShare';
import { Button } from '../component/button';
import { Input } from '../component/input';
import { AppStore } from '../store/app-store';
import { Stores } from '../store/stores';

type ScreenPageOwnProps = { path: string };
type ScreenPageStoreProps = { appStore: AppStore };
type ScreenPageProps = ScreenPageOwnProps & ScreenPageStoreProps;

class ScreenPageComp extends Component<ScreenPageProps, {}> {
  componentDidMount() {
    if (process.env.SYNCRO) {
      screenShareApi.getAttendedAccessSettings();
    }
    screenShareApi.getSessionList();
    screenShareApi.getScreenList();
  }

  render() {
    const { appStore } = this.props;

    return (
      <ScreenShare
        rtcReady={appStore.connection.rtcAvailable}
        showPresenceToUser={appStore.showPresenceToUser}
        onShowPresenceToUserChange={appStore.changeShowPresenceToUser}
      />
    );
  }
}

export const ScreenPage = inject<
  Stores,
  ScreenPageOwnProps,
  ScreenPageStoreProps,
  {}
>(({ appStore }) => ({ appStore }))(
  observer(ScreenPageComp),
) as React.ComponentClass<ScreenPageOwnProps>;

interface ScreenCoordinates {
  screenX: number;
  screenY: number;
}

function sendAltF4() {
  screenShareApi.keyDownRaw('AltLeft', 17);
  setTimeout(() => screenShareApi.keyDownRaw('F4', 67), 100);
  setTimeout(() => screenShareApi.keyUpRaw('AltLeft', 17), 500);
  setTimeout(() => screenShareApi.keyUpRaw('F4', 67), 600);
}

interface ScreenShareProps {
  rtcReady: boolean;
  showPresenceToUser: boolean;
  onShowPresenceToUserChange: (showPresense: boolean) => void;
}

enum ConnectionState {
  None,
  Connecting,
  Connected,
}

interface ScreenShareState {
  hasFrame?: boolean;
  displays?: DisplayInfo[];
  selectedDisplay?: DisplayInfo;
  maximized?: boolean;
  frame?: string;
  screenWidth?: number;
  screenHeight?: number;
  showKeyCombos?: boolean;
  autoScale?: boolean;
  connectionState?: ConnectionState;
  windowsSessions?: WindowsSession[];
  selectedWindowsSession?: number;
  isServerOs?: boolean;

  clipboardValue?: string;
  clipboardInputFocused?: boolean;

  isAttendedAccessEnabled?: boolean;
  remainingAttendedAccessSeconds?: number;
  requestingAccess?: boolean;
}

const fallbackDisplay: DisplayInfo = {
  deviceName: 'Fallback',
  friendlyName: '',
  attached: false,
  currentResolution: null,
  primary: true,
  disconnect: false,
  multiDriver: false,
  mirroringDriver: false,
  modesPruned: false,
  remote: false,
  removable: false,
  supportedResolutions: {},
  vgaCompatible: true,
};

class ScreenShare extends Component<ScreenShareProps, ScreenShareState> {
  constructor(props) {
    super(props);
    this.clipboardTextbox = createRef();
  }
  state: ScreenShareState = {
    autoScale: true,
    connectionState: ConnectionState.None,
    displays: [fallbackDisplay],
    selectedDisplay: fallbackDisplay,
    selectedWindowsSession: null,
    windowsSessions: [
      {
        id: null,
        name: 'Console',
        type: 'Console',
        userName: '',
      },
    ],
  };

  touchCancel: boolean = false;
  canvas: HTMLDivElement;
  renderer: PIXI.WebGLRenderer | PIXI.CanvasRenderer;
  renderTexture: PIXI.RenderTexture;
  keyCombos: HTMLDivElement;
  clipboardTextbox: RefObject<HTMLInputElement>;
  meter: FPSMeter | null;
  failures: number = 0;
  remainingAccessInterval?: number;

  onBlur() {
    screenShareApi.resetDownKeys();
  }

  onFocus() {
    screenShareApi.resetDownKeys();
  }

  componentDidMount() {
    window.addEventListener('blur', this.onBlur);
    window.addEventListener('focus', this.onFocus);

    if (!process.env.SYNCRO) {
      this.setState({ isAttendedAccessEnabled: false });
    }

    this.renderer = PIXI.autoDetectRenderer({
      transparent: true,
    });

    screenEvents.frame.attach((tile: ScreenTile) => {
      if (this.canvas) {
        const { x, y, top, bottom, left, right, image, fullScreen } = tile;
        const width = right - left;
        const height = bottom - top;
        /*
        if (this.renderTexture != null)
          console.debug(`frame: ${width} ${height} ${fullScreen} render: ${this.renderTexture.width} ${this.renderTexture.height}`);
        else
          console.debug(`frame: ${width} ${height} ${fullScreen}`);
        */
        if (
          this.renderTexture == null ||
          this.renderTexture.height < height ||
          this.renderTexture.width < width ||
          fullScreen
        ) {
          this.renderTexture = PIXI.RenderTexture.create(width, height);
        }

        var blob = new Blob([image], { type: 'image/jpeg' });
        var imageUrl = URL.createObjectURL(blob);

        const img = new Image(width, height);
        img.src = imageUrl;
        img.onload = () => {
          const texture = new PIXI.Texture(new PIXI.BaseTexture(img));
          const sprite = new PIXI.Sprite(texture);
          sprite.x = x;
          sprite.y = y;
          this.renderer.render(sprite, this.renderTexture, false);

          setTimeout(() => {
            texture.destroy(true);
            URL.revokeObjectURL(img.src);
          }, 200);

          if (tile.isRender) {
            this.renderScreen();
          }
        };
      }
    });

    screenEvents.clipboardChanged.attach((value: string) => {
      console.log(`clipboard changed to {value}`);
      this.setState({ clipboardValue: value });
    });

    screenEvents.cursorIconChanged.attach((cursor: CursorInfo) => {
      if (!this.canvas) {
        return;
      }
      if (cursor.cssOverride) {
        this.canvas.style.cursor = cursor.cssOverride;
      } else if (cursor.imageBase64.length == 0) {
        this.canvas.style.cursor = 'default';
      } else {
        this.canvas.style.cursor = `url('data:image/png;base64,${
          cursor.imageBase64
        }') ${cursor.hotSpotX} ${cursor.hotSpotY}, default`;
      }
    });

    screenEvents.frameData.attach(
      (data: FrameData & { frameFailed: boolean }) => {
        if (data.frameFailed) {
          if (this.failures < 4) {
            screenShareApi.requestFrame();
            this.failures++;
          }
          return;
        }

        if (this.state.connectionState == ConnectionState.Connected) return;

        this.setState({
          connectionState: ConnectionState.Connected,
          screenWidth: data.screenBounds.width,
          screenHeight: data.screenBounds.height,
        });

        logger.track('screenshare_connected', undefined, {
          timeSince: 'screenshare_connect_click',
        });
      },
    );
    screenEvents.start.attach(() => {
      console.log('login');
      this.bindKeys();
      /*
            tryUntil(() => !!this.state.screenWidth, (tryNumber, last) => {
                //LOG console.log("try")
                screenShareApi.requestFrame()
            }, 5, 2000, () => {
                this.setState({remoteConnecting: false})
                messageActions.msg("danger", "Connection timed out.")
            })
*/
    });
    screenEvents.disconnect.attach(() => {
      console.log('disconnect');
      this.setState({
        screenWidth: 0,
        screenHeight: 0,
        hasFrame: false,
        connectionState: ConnectionState.None,
      });
      this.unbindKeys();
    });

    screenEvents.displayListReceived.attach(displays => {
      if (displays.length === 0) {
        console.warn('Got zero displays, gonna use the default one');
        return;
      }

      const display =
        displays.find(
          p => p.deviceName == this.state.selectedDisplay.deviceName,
        ) || displays.find(p => p.primary);

      this.setState({
        displays: displays,
        screenWidth: display.currentResolution.width,
        screenHeight: display.currentResolution.height,
        selectedDisplay: display,
      });
      console.log('Got displays info', displays);
    });

    screenEvents.sessionListReceived.attach(sessionInfo => {
      this.setState({
        isServerOs: sessionInfo.isServerOs,
      });

      if (sessionInfo.sessionList.length === 0) {
        console.warn('Session list is empty.  Defaulting to console.');
        return;
      }
      this.setState({
        windowsSessions: sessionInfo.sessionList,
        selectedWindowsSession: sessionInfo.sessionList[0].id,
      });
    });

    screenEvents.attendedAccessSettingsReceived.attach(settings => {
      this.setState({
        isAttendedAccessEnabled: settings.isEnabled,
        remainingAttendedAccessSeconds: settings.allowedSecondsRemaining,
        requestingAccess: false,
      });

      if (this.remainingAccessInterval) {
        window.clearInterval(this.remainingAccessInterval);
      }

      if (settings.isEnabled && settings.allowedSecondsRemaining > 0) {
        this.remainingAccessInterval = window.setInterval(
          this.updateRemainingAccessCountdown,
          500,
        );
      }
    });
  }

  renderScreen() {
    let stage = new PIXI.Sprite(this.renderTexture);

    if (this.state.autoScale) {
      const rect = this.canvas.getBoundingClientRect();
      const ratio = Math.min(
        rect.width < this.state.screenWidth
          ? rect.width / this.state.screenWidth
          : 1,
        rect.height < this.state.screenHeight
          ? rect.height / this.state.screenHeight
          : 1,
      );

      stage.scale.x = stage.scale.y = ratio;
    }

    this.renderer.resize(stage.width, stage.height);
    this.renderer.render(stage);
    if (this.meter) this.meter.tick();
  }

  bindKeys() {
    document.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('keyup', this.onKeyUp);
  }

  unbindKeys() {
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('keyup', this.onKeyUp);
  }

  componentWillUnmount() {
    screenShareApi.disconnect();
    _.forOwn(screenEvents, event => {
      event.detach();
    });
    this.unbindKeys();
    window.removeEventListener('resize', this.onResize);
    this.props.onShowPresenceToUserChange(false);
  }

  cancelEvents(e: React.SyntheticEvent<HTMLDivElement> | Event) {
    e.preventDefault();
    e.stopPropagation();
  }

  getScreenCoords = (e: {
    clientX: number;
    clientY: number;
  }): ScreenCoordinates => {
    let target = this.renderer.view as HTMLCanvasElement;
    let rect = target.getBoundingClientRect();
    let vX = e.clientX - rect.left;
    let vY = e.clientY - rect.top;
    if (vX > rect.width || vY > rect.height) {
      return undefined;
    }

    let sW = this.state.screenWidth || 1;
    let sH = this.state.screenHeight || 1;

    let screenX = vX * sW / rect.width;
    let screenY = vY * sH / rect.height;
    /*
    console.debug(
      `getScreenCoords: ${vX} ${vY} of ${rect.width} ${
        rect.height
      } to ${sW} ${sH} => ${screenX} ${screenY}`,
    );
    */
    return {
      screenX,
      screenY,
    };
  };

  transformMouse = (e: React.MouseEvent<HTMLDivElement>) => {
    const screenCoords = this.getScreenCoords(e);

    if (!screenCoords) return;

    let transformed = Object.assign({}, e, screenCoords);

    return transformed as React.MouseEvent<HTMLDivElement> & ScreenCoordinates;
  };

  tranformTouch = (e: React.TouchEvent<HTMLDivElement>) => {
    const screenCoords = this.getScreenCoords(
      e.changedTouches.item(e.changedTouches.length - 1),
    );

    if (!screenCoords) return;

    let transformed = Object.assign({}, e, screenCoords);

    return transformed as React.TouchEvent<HTMLDivElement> & ScreenCoordinates;
  };

  updateRemainingAccessCountdown = () => {
    var newRemaining = Math.max(
      this.state.remainingAttendedAccessSeconds - 0.5,
      0,
    );
    this.setState({
      remainingAttendedAccessSeconds: newRemaining,
    });
    if (newRemaining == 0) {
      window.clearInterval(this.remainingAccessInterval);
    }
  };

  onKeyDown = (e: KeyboardEvent) => {
    const { clipboardInputFocused, clipboardValue } = this.state;

    // We want to catch input to clipboard input instead of passing it to remote host
    if (clipboardInputFocused) {
      return;
    }

    if (isPasteHotkey(e)) {
      screenShareApi.paste(e, clipboardValue);
    } else {
      this.cancelEvents(e);
      screenShareApi.keyDown(e);
    }
  };

  onKeyUp = (e: KeyboardEvent) => {
    const { clipboardInputFocused } = this.state;

    // We want to catch input to clipboard input instead of passing it to remote host
    if (clipboardInputFocused) {
      return;
    }

    this.cancelEvents(e);
    screenShareApi.keyUp(e);
  };

  processMouseOrTouch(
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
  ) {
    const { clipboardInputFocused } = this.state;

    const isTouchEvent = (
      e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
    ): e is React.TouchEvent<HTMLDivElement> =>
      !!(e as React.TouchEvent<HTMLDivElement>).changedTouches;

    const isWheelEvent = (
      e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
    ): e is React.WheelEvent<HTMLDivElement> =>
      !!(e as React.WheelEvent<HTMLDivElement>).deltaMode;

    // We want to take focus off the clipboard input if we click outside it
    if (clipboardInputFocused) {
      this.clipboardTextbox.current.blur();
    }

    if (!isWheelEvent(e) && !isTouchEvent(e)) {
      this.cancelEvents(e);
    }

    return !isTouchEvent(e) ? this.transformMouse(e) : this.tranformTouch(e);
  }

  onResize = () => {
    //console.log(this.canvas, this.keyCombos)
    if (this.canvas && this.keyCombos) {
      const rect = this.canvas.getBoundingClientRect();
      this.keyCombos.style.top = rect.top + 'px';
      this.keyCombos.style.left = rect.left + 'px';
    }
  };

  componentDidUpdate() {
    this.onResize();
  }

  keys() {
    const display = this.state.showKeyCombos ? 'block' : 'none';
    const s: React.CSSProperties = {
      display,
      position: 'fixed',
      marginLeft: 10,
      marginTop: 10,
    };
    const ps: React.CSSProperties = {
      cursor: 'pointer',
    };
    return (
      <div
        style={s}
        ref={ref => {
          if (ref) this.keyCombos = ref;
        }}
      >
        <div>
          <kbd
            style={ps}
            onClick={() => {
              //sendKeyCombo(17, 18, 46)
              screenShareApi.ctrlAltDel();
            }}
          >
            ctrl + alt + delete
          </kbd>
        </div>
        <div onClick={() => sendAltF4()}>
          <kbd style={ps}>alt + f4</kbd>
        </div>
      </div>
    );
  }

  frameImg() {
    let style = { width: '100%', height: '100%' };
    return (
      <div
        style={style}
        tabIndex={1}
        ref={ref => {
          this.canvas = ref;
          if (this.canvas) {
            this.canvas.appendChild(this.renderer.view);
            let rect = this.canvas.getBoundingClientRect();
            if (
              rect.width != this.renderer.view.width ||
              rect.height != this.renderer.view.height
            ) {
              console.log('force render screen!');
              this.renderScreen();
            }
          }
        }}
        onMouseMove={e => {
          let res = this.processMouseOrTouch(e);
          if (!res) return;

          let { screenX, screenY } = res;
          screenShareApi.mouse.move(screenX, screenY);
        }}
        onContextMenu={e => {
          this.processMouseOrTouch(e);
          //screenShareApi.mouse.rightClick();
        }}
        onMouseDown={e => {
          this.processMouseOrTouch(e);
          console.log(e.button);
          if (e.button === 0) {
            screenShareApi.mouse.down();
          } else if (e.button === 2) {
            screenShareApi.mouse.rightDown();
          }
        }}
        onMouseUp={e => {
          this.processMouseOrTouch(e);
          if (e.button === 0) {
            screenShareApi.mouse.up();
          } else if (e.button === 2) {
            screenShareApi.mouse.rightUp();
          }
        }}
        onWheel={e => {
          this.processMouseOrTouch(e);
          screenShareApi.mouse.wheel(e.deltaY);
        }}
        onTouchCancel={e => {
          this.touchCancel = true;
        }}
        onTouchEnd={e => {
          if (this.touchCancel) return;
          this.touchCancel = false;

          let res = this.processMouseOrTouch(e);
          if (!res) return;

          let { screenX, screenY } = res;
          screenShareApi.mouse.leftClick(screenX, screenY);
        }}
      />
    );
  }

  requestFullScreen = element => {
    // Supports most browsers and their versions.
    var requestMethod =
      element.requestFullScreen ||
      element.webkitRequestFullScreen ||
      element.mozRequestFullScreen ||
      element.msRequestFullScreen;

    if (requestMethod) {
      // Native full screen.
      requestMethod.call(element);
    }
  };

  maximize = () => {
    this.setState({
      maximized: true,
    });
  };

  restoreSize = () => {
    this.setState({
      maximized: false,
    });
  };

  copyClipboardToHost = () => {
    this.clipboardTextbox.current.select();

    try {
      var successful = document.execCommand('copy');
      var msg = successful ? 'successful' : 'unsuccessful';
      console.log('Copying text command was ' + msg);
    } catch (err) {
      console.error('Oops, unable to copy');
    }
  };

  clipboardControls = () => {
    return (
      <div style={{ display: 'flex' }}>
        <Input
          type="text"
          inputDomRef={this.clipboardTextbox}
          value={this.state.clipboardValue}
          onFocus={this.handleClipboardFocus}
          onBlur={this.handleClipboardBlur}
          onChange={this.handleClipboardChange}
        />
        <Button
          icon="clipboard"
          onClick={this.copyClipboardToHost}
          aria-label="Copy to clipboard"
          size="small"
        />
      </div>
    );
  };

  handleClipboardFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    this.setState({ clipboardInputFocused: true });
  };

  handleClipboardBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    this.setState({ clipboardInputFocused: false });
  };

  // TODO MB Update react typying and use React.ChangeEvent
  handleClipboardChange = (event: any) => {
    event.preventDefault();
    const text = event.target.value;
    this.setState({ clipboardValue: text });
    this.debouncedSetClipboardData(text);
  };

  debouncedSetClipboardData = debounce(
    text => screenShareApi.setClipboardData(text),
    500,
  );

  resizeButtons = () => {
    if (this.state.maximized) {
      return (
        <button className="text-button" onClick={this.restoreSize}>
          <span className="glyphicon glyphicon-resize-small" />
        </button>
      );
    } else {
      return (
        <button className="text-button" onClick={this.maximize}>
          <span className="glyphicon glyphicon-resize-full" />
        </button>
      );
    }
  };

  connectionType = () => {
    if (this.props.rtcReady) {
      return <span>Direct</span>;
    } else {
      return (
        <span>
          <span data-tip data-for="relay-tooltip">
            Relayed
          </span>
          <ReactTooltip
            id="relay-tooltip"
            place="top"
            effect="solid"
            type="dark"
          >
            <span>
              Unable to make a direct connection due to network topology
            </span>
          </ReactTooltip>
        </span>
      );
    }
  };

  goFullScreen = () => {
    var rd = document.getElementById('remoteDesktop');
    if (!rd) return;

    this.setState({ autoScale: true });
    this.requestFullScreen(rd);
  };

  fullScreenButton = () => {
    return (
      <button className="text-button" onClick={this.goFullScreen}>
        <span className="glyphicon glyphicon-fullscreen" />
      </button>
    );
  };

  toggleAutoScale = () => {
    this.setState({ autoScale: !this.state.autoScale });
  };

  autoScaleButton = () => {
    return (
      <button className="text-button" onClick={this.toggleAutoScale}>
        <span className="glyphicon glyphicon-blackboard" />
      </button>
    );
  };

  handleCtrlAltDel = () => {
    console.log('CTRL-ALT-DELETE!');
    screenShareApi.ctrlAltDel();
  };

  connected() {
    if (this.state.connectionState == ConnectionState.Connected) {
      return (
        <div className="proxima-nova-14">
          {/* leaving this here for example, once ctrl-alt-delete is implemented, we
                    can remove this shiiiiiiit
                 <button className="text-button" onClick={screenShareApi.disconnect}>
                    disconnect
                </button> */}
          <button className="text-button" onClick={this.handleCtrlAltDel}>
            CTRL-ALT-DLT
          </button>
          &nbsp;
          {this.resizeButtons()}
          &nbsp;
          {this.fullScreenButton()}
          &nbsp;
          {this.autoScaleButton()}
          &nbsp; &nbsp;
          {this.connectionType()}
        </div>
      );
    }
    return <div className="proxima-nova-14">{this.connectionType()}</div>;
  }

  frame() {
    let style = { width: '100%', height: '100%' };
    return (
      <div id="remoteDesktop" style={style} className="fixed">
        {this.frameImg()}
        {this.keys()}
      </div>
    );
    // if(navigator.userAgent.toLowerCase().indexOf('firefox') > -1){
    //     return <Center noHeight style={{flexGrow: 1, "margin-top": "20%"}}>
    //                 <div className="">
    //                     <span>Currently only Chrome is supported for remote control.</span>
    //                 </div>
    //             </Center>

    // }
  }

  handleRequestAccessClick = () => {
    // Don't allow multiple pending requests.  Check this on
    // the remote agent too.
    if (this.state.requestingAccess) {
      return;
    }

    this.setState({
      requestingAccess: true,
    });

    // Toggle back after a bit in case response never comes back.
    window.setTimeout(() => {
      this.setState({
        requestingAccess: false,
      });
    }, 10000);

    screenShareApi.sendRemoteAccessRequest(
      this.props.showPresenceToUser,
      this.state.selectedWindowsSession,
    );
  };

  handleConnectClick = () => {
    if (this.state.connectionState == ConnectionState.Connecting) {
      console.log('Connection in progress.  Aborting.');
      return;
    }

    logger.track('screenshare_connect_click', {
      page_name: 'screen',
      element: 'click',
    });
    this.setState({ connectionState: ConnectionState.Connecting });
    screenShareApi.start(this.state.selectedWindowsSession);
    screenShareApi.setMonitor(
      this.state.selectedDisplay ? this.state.selectedDisplay.deviceName : '',
    );
    if (this.props.showPresenceToUser) screenShareApi.showPresenceToUser(true);
  };

  handleDisplayNumberClick = displayInfo => {
    screenShareApi.setMonitor(displayInfo.deviceName);
    this.setState({
      screenWidth: displayInfo.currentResolution.width,
      screenHeight: displayInfo.currentResolution.height,
      selectedDisplay: displayInfo,
    });
  };

  renderDisplayNumbersControls = () => {
    return (
      <div>
        {this.state.displays.map((display, displayIndex) => (
          <div
            key={'display' + displayIndex}
            className="btn btn-link btn-sm"
            style={{
              backgroundColor:
                display.deviceName === this.state.selectedDisplay.deviceName
                  ? '#ccc'
                  : '',
            }}
            onClick={() => this.handleDisplayNumberClick(display)}
          >
            Display {displayIndex + 1}
          </div>
        ))}
        {this.clipboardControls()}
      </div>
    );
  };

  renderRemainingAccess(): React.ReactNode {
    return (
      <div style={{ marginTop: '2em' }}>
        <div style={{ fontWeight: 'bold' }}>Remaining Access Time</div>
        <div>
          {Math.floor(this.state.remainingAttendedAccessSeconds / 60 / 60)}h,&nbsp;
          {Math.floor(this.state.remainingAttendedAccessSeconds / 60) % 60}m,&nbsp;
          {Math.floor(this.state.remainingAttendedAccessSeconds % 60)}s
        </div>
      </div>
    );
  }

  renderConnectFrame() {
    if (
      this.state.isAttendedAccessEnabled == false ||
      this.state.remainingAttendedAccessSeconds > 0
    ) {
      return (
        <div>
          <div className="create-desktop" onClick={this.handleConnectClick}>
            <span>
              {this.state.connectionState == ConnectionState.Connecting
                ? 'Connecting'
                : 'Connect'}
            </span>
          </div>
          {this.state.isAttendedAccessEnabled
            ? this.renderRemainingAccess()
            : ''}
        </div>
      );
    } else if (
      this.state.isAttendedAccessEnabled &&
      this.state.remainingAttendedAccessSeconds <= 0
    ) {
      return (
        <div className="create-desktop" onClick={this.handleRequestAccessClick}>
          <span>
            {this.state.requestingAccess
              ? 'Requesting Access'
              : 'Request Access'}
          </span>
        </div>
      );
    } else {
      return <div>Retrieving settings...</div>;
    }
  }

  render() {
    const { showPresenceToUser, onShowPresenceToUserChange } = this.props;

    const showToolbox = this.state.connectionState == ConnectionState.Connected;

    const stubLabel = (
      <div
        onClick={() =>
          this.setState({
            showKeyCombos: !this.state.showKeyCombos,
          })
        }
      >
        <div>Remote desktop</div>
      </div>
    );

    var screenClass = 'screen-page';
    if (this.state.maximized) screenClass += ' fullscreen';
    return (
      <div className={screenClass} style={{ height: '100%' }}>
        <div className="kabuto-live-panel" style={{ height: '100%' }}>
          {showToolbox ? (
            <ScreenShareToolbox
              displays={this.state.displays}
              selectedDisplay={this.state.selectedDisplay}
              onDisplaySelect={this.handleDisplayNumberClick}
              clipboardInput={this.clipboardControls()}
              refFpsMeter={meter => (this.meter = meter)}
              showPresenceToUser={showPresenceToUser}
              onShowPresenceToUserChange={onShowPresenceToUserChange}
              connectionType={this.connectionType()}
              onCtrlAlDel={this.handleCtrlAltDel}
              isMaximized={this.state.maximized}
              onChangeIsMaximized={isMaximized =>
                isMaximized ? this.maximize() : this.restoreSize()
              }
              isZoomedIn={!this.state.autoScale}
              onChangeIsZoomedIn={this.toggleAutoScale}
              onFullScreen={this.goFullScreen}
            />
          ) : (
            <ScreenShareHeader connectionType={this.connectionType()} />
          )}
          <div
            style={{
              height: '100%',
              overflow: this.state.autoScale ? 'hidden' : 'auto',
            }}
          >
            {this.state.connectionState == ConnectionState.Connected ? (
              this.frame()
            ) : (
              <Center noHeight style={{ flexGrow: 1, marginTop: '20%' }}>
                {this.renderConnectFrame()}
                {this.state.isServerOs ? (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '200px',
                      gridTemplateRows: 'auto',
                      marginTop: '20px',
                    }}
                  >
                    <label>Target Session:</label>
                    <select
                      size={Math.max(3, this.state.windowsSessions.length)}
                      style={{
                        minWidth: '200px',
                        justifySelf: 'center',
                        borderRadius: '5px',
                        overflowY: 'auto',
                        maxHeight: '150px',
                      }}
                      onChange={ev => {
                        this.setState({
                          selectedWindowsSession: ev.currentTarget.value
                            ? Number(ev.currentTarget.value)
                            : null,
                        });
                      }}
                    >
                      {this.state.windowsSessions.map(x => {
                        return (
                          <option value={x.id} style={{ padding: '10px' }}>
                            {`${x.type} (User: ${x.userName || 'Not Found'})`}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                ) : (
                  ''
                )}
              </Center>
            )}
          </div>
        </div>
      </div>
    );
  }
}
